import Modal, { ModalProps } from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import { ReactNode, useContext, createContext, useState } from 'react';
import HButton from '../components/Elements/HButton';

type ConfirmDialogProps = {
    title: string;
    message: string;
    yesButton?: boolean;
    noButton?: boolean;
    image?: React.ReactNode;
    yesButtonTitle?: string;
    noButtonTitle?: string;
    isVisible: boolean;
    onClose: () => void;
    onConfirm: () => void;
} & ModalProps;

type ConfirmDialogContextType = {
    modal: ConfirmDialogProps;
    showConfirmModal: (props: ConfirmDialogProps) => void;
    hideConfirmModal: () => void;
};

const defaultConfirmDialogContextValue: ConfirmDialogContextType = {
    modal: {
        title: '',
        message: '',
        image: undefined,
        isVisible: false,
        yesButtonTitle: 'button.remove',
        noButtonTitle: 'button.cancel',
        yesButton: true,
        noButton: true,
        onClose: () => {},
        onConfirm: () => {},
    },
    showConfirmModal: () => {},
    hideConfirmModal: () => {},
};

const ConfirmDialogContext = createContext<ConfirmDialogContextType>(defaultConfirmDialogContextValue);

export const ConfirmDialogProvider = ({ children }: { children: ReactNode }) => {
    const [modalState, setModalState] = useState<ConfirmDialogProps>(defaultConfirmDialogContextValue.modal);

    const showConfirmModal = (props: ConfirmDialogProps) => {
        setModalState({ ...props, isVisible: true });
    };

    const hideConfirmModal = () => {
        setModalState((prevState) => ({ ...prevState, isVisible: false }));
    };

    const contextValue: ConfirmDialogContextType = {
        modal: modalState,
        showConfirmModal,
        hideConfirmModal,
    };

    return (
        <ConfirmDialogContext.Provider value={contextValue}>
            {children}
            <ConfirmDialogModal />
        </ConfirmDialogContext.Provider>
    );
};

export const useConfirmation = (): ConfirmDialogContextType => {
    const context = useContext(ConfirmDialogContext);
    if (!context) {
        throw new Error('useConfirmation must be used within an ConfirmProvider');
    }
    return context;
};

export const ConfirmDialogModal = () => {
    const { t } = useTranslation();

    const { modal } = useConfirmation();
    const {
        isVisible,
        title,
        message,
        image,
        yesButton = true,
        noButton = true,
        yesButtonTitle = 'button.remove',
        noButtonTitle = 'button.cancel',
        onClose,
        onConfirm,
        ...rest
    } = modal;

    if (!isVisible) return null;

    return (
        <Modal show={isVisible} onHide={onClose} {...rest} className="confirmation-modal">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            {(message || image) && (
                <Modal.Body className="confirmation-body">
                    {image && <div className="mb-2 confirmation-image-wrapper">{image}</div>}
                    {message}
                </Modal.Body>
            )}
            <Modal.Footer>
                {yesButton && (
                    <HButton
                        customClass="d-block"
                        title={t(yesButtonTitle)}
                        btnType="red"
                        width={150}
                        onClick={onConfirm}
                    />
                )}
                {noButton && (
                    <HButton
                        customClass="d-block ms-2"
                        title={t(noButtonTitle)}
                        btnType="grey"
                        width={150}
                        onClick={onClose}
                    />
                )}
            </Modal.Footer>
        </Modal>
    );
};
